import * as THREE from 'three';
import { CONFIG } from '../config';
import { hustleOpen } from '../content/hustles';
import type { Choice, Scene } from '../content/types';
import type { NpcAgent } from '../entities/NpcManager';
import { absoluteMinutes, rentDueDay, weekOf } from '../state/rules';
import type { Interactions } from '../systems/Interactions';
import { SPOTS } from '../world/layout';
import {
  JUNK_PIECES,
  junkChunk,
  LOT10,
  RENTAL_FIXES,
  type Lot10State,
  type RentalFix,
  type RentalStage,
} from '../world/lot10';
import type { Activities } from './Activities';
import type { GameServices } from './services';

export const LANDLORD = {
  lease: 300,
  trailer: 600,
  rent: 120,
  junkPay: 5,
  sell: 1800,
  sellRented: 2200,
} as const;
/** Game minutes from Big Dave's phone call to a trailer on your lot. */
const DELIVERY_MINUTES = 90;

const FIXES: Record<
  RentalFix,
  { money?: number; scrap?: number; label: string; done: string; seconds: number }
> = {
  steps: {
    scrap: 4,
    label: 'Build porch steps (cinder blocks and scrap, -4 :scrap:)',
    done: 'Three cinder blocks and a prayer. Up to code? There is no code.',
    seconds: 2.2,
  },
  windows: {
    money: 30,
    label: 'Replace the busted windows (plexiglass, -$30)',
    done: 'Plexiglass, duct tape, a little caulk. You can see out of it. Mostly.',
    seconds: 2.2,
  },
  paint: {
    money: 25,
    label: 'Paint the siding (-$25)',
    done: "Two coats, one of them on purpose. It's a whole new trailer.",
    seconds: 3,
  },
  skirting: {
    money: 35,
    label: 'Put up lattice skirting (-$35)',
    done: 'Lattice all the way around. The possums will have to find another trailer.',
    seconds: 2.5,
  },
  roof: {
    money: 40,
    label: 'Patch the roof (a blue tarp and roof tar, -$40)',
    done: 'A blue tarp, tarred down at the corners. The traditional way.',
    seconds: 2.6,
  },
};

/** When a week's rent comes due: that Friday at 5 PM (game minutes since day 1). */
const rentDue = (week: number): number => absoluteMinutes(rentDueDay(week), CONFIG.rentCheckMinutes);

/** Rhonda moves in the morning after you list it; her first rent is the first Friday after that. */
export function firstRentWeek(listedDay: number): number {
  const moveIn = absoluteMinutes(listedDay + 1, 0);
  let week = weekOf(listedDay + 1);
  while (rentDue(week) < moveIn) week++;
  return week;
}

const PAINTS: ReadonlyArray<[string, string]> = [
  ["Robin's Egg", '#bfe3ea'],
  ['Mint Julep', '#cdeccd'],
  ['Butter', '#f3e3a1'],
  ['Pepto Pink', '#f6c6d8'],
];

const RHONDA = [
  "'Toilet's runnin' again, landlord. I jiggled it. It jiggled back.'",
  "'Rent's under your door Friday. In quarters. Don't count 'em in front of me.'",
  "'Nicest place I ever lived. The bar was low. You cleared it. Barely.'",
  "'Somebody keeps leavin' a casserole on my steps. I'm not complainin'.'",
  "'If a Camaro with one door pulls up, I'm not here. That's my ex. He owes me a microwave.'",
  "'You ain't like those Apex people. You fixed the windows. They'd have raised the rent on 'em.'",
];

/**
 * Landlord of Lot 10, the big real-estate scheme: lease the burnt-out lot from Apex
 * (through Darlene), haul the wreck off to Junkyard Jerry piece by piece, have Big Dave
 * drop a repo single-wide on it, fix that up (steps, windows, paint, skirting, roof),
 * then rent it to Rhonda Mae for $120 every Friday, or flip it to Big Dave.
 */
export class Landlord {
  private carrying: THREE.Group | null = null;
  private applied = '';
  private readonly tmp = new THREE.Vector3();

  constructor(
    private readonly s: GameServices,
    act: Activities,
  ) {
    act.talkChoices.push((agent) => this.darlene(agent));
  }

  get stage(): RentalStage {
    const v = this.s.store.flag('rental_stage');
    return typeof v === 'string' ? (v as RentalStage) : 'none';
  }

  private set stage(v: RentalStage) {
    this.s.store.setFlag('rental_stage', v);
  }

  private get junk(): number {
    return Number(this.s.store.flag('rental_junk') ?? 0);
  }

  get fixed(): RentalFix[] {
    const v = this.s.store.flag('rental_fixed');
    return typeof v === 'string' && v
      ? (v.split(',').filter((f) => RENTAL_FIXES.includes(f as RentalFix)) as RentalFix[])
      : [];
  }

  private state(): Lot10State {
    const st = this.s.store;
    const since = Number(st.flag('rental_since') ?? 0);
    const h = st.minutes / 60;
    return {
      stage: this.stage,
      // The piece over your head is off the lot already.
      junk: this.junk + (this.carrying ? 1 : 0),
      fixed: this.fixed,
      paint: String(st.flag('rental_paint') ?? '#bfe3ea'),
      tenantOut: st.day > since && h >= 9 && h < 21,
    };
  }

  /** New game or a load: Lot 10 looks like the save says. */
  reset(): void {
    if (this.carrying && this.s.player.carrying === this.carrying) this.s.player.setCarrying(null);
    this.carrying = null;
    this.applied = '';
    this.sync();
  }

  private sync(): void {
    const st = this.state();
    const key = JSON.stringify(st);
    if (key === this.applied) return;
    this.applied = key;
    this.s.world.lot10.apply(st);
  }

  register(interactions: Interactions): void {
    JUNK_PIECES.forEach((name, i) =>
      interactions.add({
        id: `lot10-junk-${i}`,
        at: () => LOT10.junk[i] ?? LOT10.sign,
        radius: 1.7,
        priority: 0.8,
        prompt: () =>
          this.stage === 'leased' && this.junk === i && !this.carrying
            ? `Haul off the burnt ${name} (${i + 1}/${JUNK_PIECES.length})`
            : null,
        action: () => this.pickUpJunk(i),
        highlight: () => this.s.world.lot10.pieces[i] ?? null,
        icon: ':shack:',
      }),
    );
    interactions.add({
      id: 'lot10-sign',
      at: () => LOT10.sign,
      radius: 2,
      priority: 0.8,
      prompt: () => {
        switch (this.stage) {
          case 'cleared':
            return `:telephone: Call Big Dave: a repo single-wide, delivered ($${LANDLORD.trailer})`;
          case 'ready':
            return 'Lot 10 is ready: rent it out or sell it';
          case 'rented':
            return `Sell Lot 10 to Big Dave, tenant and all ($${LANDLORD.sellRented.toLocaleString()})`;
          default:
            return null;
        }
      },
      action: () => this.signAction(),
      icon: ':yard-sign:',
    });
    for (const fix of RENTAL_FIXES) {
      const def = FIXES[fix];
      interactions.add({
        id: `lot10-fix-${fix}`,
        at: () => LOT10.fixes[fix],
        radius: 1.15,
        priority: 0.9,
        prompt: () => (this.stage === 'trailer' && !this.fixed.includes(fix) ? def.label : null),
        action: () => this.fix(fix),
        highlight: () => this.s.world.lot10.trailerModel,
        icon: ':hammer:',
      });
    }
    interactions.add({
      id: 'lot10-tenant',
      at: () => LOT10.tenant,
      radius: 2,
      prompt: () =>
        this.stage === 'rented' && this.state().tenantOut ? 'Talk to Rhonda Mae (your tenant)' : null,
      action: () => {
        this.s.ui.modal.show({
          title: 'Rhonda Mae',
          art: ':leopard:',
          text: this.s.rng.pick(RHONDA),
          choices: [],
          leave: "You're a good tenant, Rhonda",
        });
      },
      icon: ':speech:',
    });
  }

  /** Darlene's the one with the Apex paperwork. */
  private darlene(agent: NpcAgent): Choice[] {
    if (agent.def.id !== 'darlene' || this.stage !== 'none') return [];
    const { store } = this.s;
    if (!hustleOpen(store.state, 'landlord')) return [];
    return [
      {
        label: ':shack: Ask about Lot 10',
        run: (): Scene => ({
          title: 'Darlene',
          speaker: { id: 'darlene', name: 'Park Manager', voice: 330 },
          text: `'Lot 10? Been sittin' burnt since the Hendersons' turkey fryer incident. Apex'll lease it to anybody with a pulse and $${LANDLORD.lease}.\n\nYou clear the junk off, you get a trailer on it, you fix it up, you rent it out. Then you're a landlord, sugar. Don't let it go to your head. I've seen what it does to people.'`,
          choices: [
            {
              label: 'Lease Lot 10',
              hint: `-$${LANDLORD.lease}`,
              requires: ({ store: st }) => st.canAfford(LANDLORD.lease) || `Need $${LANDLORD.lease}`,
              run: ({ store: st }) => {
                st.spend(LANDLORD.lease);
                this.stage = 'leased';
                st.setFlag('rental_junk', 0);
                this.sync();
                this.s.sfx('cash');
                return "Darlene stamps three forms with the same stamp and hands you a key to a lot that doesn't have a door. 'It's yours. Well, it's Apex's. You're payin' for the privilege.'\n\nFirst: haul that burnt wreck off to Junkyard Jerry, one piece at a time. He pays scrap.";
              },
            },
          ],
          leave: 'Let me think on it',
        }),
      },
    ];
  }

  private pickUpJunk(i: number): void {
    const { player, store } = this.s;
    if (this.stage !== 'leased' || this.junk !== i || this.carrying) return;
    if (player.carrying || player.pushing) {
      store.toast('Your hands are full.', 'bad', ':shack:');
      return;
    }
    const chunk = junkChunk(i);
    player.setCarrying(chunk);
    this.carrying = chunk;
    this.sync();
    this.s.sfx('thud');
    store.toast(
      `You hoist the burnt ${JUNK_PIECES[i]}. Junkyard Jerry's drop-off, north end of the park.`,
      'info',
      ':shack:',
    );
  }

  private signAction(): void {
    const { store, ui } = this.s;
    switch (this.stage) {
      case 'cleared':
        if (!store.spend(LANDLORD.trailer)) {
          store.toast(
            `Big Dave wants $${LANDLORD.trailer} up front. "Repo trailers don't grow on trees, chief."`,
            'bad',
            ':telephone:',
          );
          return;
        }
        this.stage = 'delivering';
        store.setFlag('rental_arrive', store.now + DELIVERY_MINUTES);
        this.s.sfx('phone');
        ui.modal.show({
          title: "Big Dave's Homes",
          art: ':telephone:',
          text: `Big Dave picks up on the first ring. 'A repo single-wide? Got a '79 that came back from Tuscaloosa. Roof leaks, windows are plywood, there's somethin' livin' under it. $${LANDLORD.trailer}, delivered. My crew'll drop it on Lot 10 in a couple hours.'`,
          choices: [],
          leave: 'Sold',
        });
        return;
      case 'ready':
        ui.modal.show({
          title: 'Lot 10',
          art: ':trailer:',
          text: "It's done. New steps, new windows, a fresh coat of paint, lattice skirting and a roof that only leaks a little. Somebody would live here. Somebody would PAY to live here.",
          choices: [
            {
              label: 'Rent it out',
              hint: `+$${LANDLORD.rent} every Friday`,
              run: () => {
                this.stage = 'rented';
                store.setFlag('rental_since', store.day);
                store.setFlag('rental_week', firstRentWeek(store.day) - 1);
                store.unlock('landlord');
                this.sync();
                return `You stick a NO VACANCY sign on top of the FOR RENT sign. By morning, a woman named Rhonda Mae has moved in with a lawn chair, a cooler and a leopard print bathrobe.\n\nShe'll pay $${LANDLORD.rent} every Friday at 5. In quarters.`;
              },
            },
            {
              label: 'Sell it to Big Dave',
              hint: `+$${LANDLORD.sell.toLocaleString()}`,
              run: () => this.sell(LANDLORD.sell),
            },
          ],
          leave: 'Let me think on it',
        });
        return;
      case 'rented':
        ui.modal.show({
          title: 'Sell Lot 10?',
          art: ':trailer:',
          text: `Big Dave will buy the lease, the trailer and Rhonda Mae's rent for $${LANDLORD.sellRented.toLocaleString()} cash. You'd stop getting $${LANDLORD.rent} a week.`,
          choices: [
            {
              label: 'Sell it',
              hint: `+$${LANDLORD.sellRented.toLocaleString()}`,
              run: () => this.sell(LANDLORD.sellRented),
            },
          ],
          leave: "Keep collectin' rent",
        });
        return;
      default:
        return;
    }
  }

  private sell(price: number): string {
    const { store } = this.s;
    store.earn(price);
    store.incr('flips');
    store.unlock('landlord');
    this.stage = 'sold';
    this.sync();
    this.s.sfx('cash');
    return `Big Dave counts out $${price.toLocaleString()} in fifties on the hood of his truck. 'Pleasure doin' business, landlord.' Lot 10 isn't yours anymore, but the money sure is.`;
  }

  private fix(fix: RentalFix): void {
    const { store, player } = this.s;
    const def = FIXES[fix];
    if (this.stage !== 'trailer' || this.fixed.includes(fix) || player.busy) return;
    if (def.scrap && !store.has('scrap', def.scrap)) {
      store.toast(
        `Need ${def.scrap} :scrap: scrap for the steps (it sparkles on the ground all over the park).`,
        'bad',
        ':hammer:',
      );
      return;
    }
    if (def.money && !store.canAfford(def.money)) {
      store.toast(`Need $${def.money} for supplies.`, 'bad', ':hammer:');
      return;
    }
    if (fix === 'paint') {
      this.s.ui.modal.show({
        title: 'Pick a Color',
        art: ':paint:',
        text: "Lucky's nephew has four gallons of mis-tinted paint he'll let go cheap. All four are, technically, colors.",
        choices: PAINTS.map(([name, color]) => ({
          label: name,
          hint: `-$${def.money}`,
          run: () => {
            store.setFlag('rental_paint', color);
            this.work(fix);
          },
        })),
        leave: 'Never mind',
      });
      return;
    }
    this.work(fix);
  }

  /** The actual work: pay for supplies, swing a hammer for a couple seconds, and it's done. */
  private work(fix: RentalFix): void {
    const { store, player } = this.s;
    const def = FIXES[fix];
    if (def.scrap) store.take('scrap', def.scrap);
    if (def.money) store.spend(def.money);
    const at = LOT10.fixes[fix];
    this.s.sfx(fix === 'paint' ? 'scratch' : 'clank');
    player.doBusy({
      pose: fix === 'paint' ? 'throw' : 'pull',
      until: this.s.now() + def.seconds,
      onDone: () => {
        store.setFlag('rental_fixed', [...this.fixed, fix].join(','));
        store.apply({ stats: { energy: -4 }, minutes: 30 });
        this.s.sfx(fix === 'paint' ? 'splash' : 'thunk');
        for (let k = 0; k < 16; k++)
          this.s.world.sparks.spawn({
            x: at.x,
            y: 1.2,
            z: at.z - 0.8,
            vx: (Math.random() - 0.5) * 2.5,
            vy: 1 + Math.random() * 2,
            vz: (Math.random() - 0.5) * 2.5,
            life: 0.7,
            size: 0.14,
            color: fix === 'paint' ? String(store.flag('rental_paint') ?? '#ffffff') : '#d8c39a',
            gravity: 7,
          });
        const left = RENTAL_FIXES.filter((f) => !this.fixed.includes(f)).length;
        if (left === 0) {
          this.stage = 'ready';
          store.toast(
            "Lot 10's all fixed up! Go to the sign out front to rent it out (or sell it).",
            'good',
            ':trailer:',
          );
        } else store.toast(`${def.done} (${left} to go)`, 'good', ':hammer:');
        this.sync();
      },
    });
  }

  update(dt: number, time: number): void {
    const { store, player } = this.s;
    // Hauling the wreck to Jerry's.
    const c = this.carrying;
    if (c) {
      if (player.carrying !== c) {
        // Dropped it somewhere (a cutscene, a nap...): it goes back on the lot.
        this.carrying = null;
        store.toast('You set the burnt junk down. It wandered back to Lot 10, somehow.', 'info', ':shack:');
      } else {
        const d = SPOTS.junkyardDrop;
        if (Math.hypot(player.position.x - d.x, player.position.z - d.z) < 3) {
          player.setCarrying(null);
          this.carrying = null;
          const n = this.junk + 1;
          store.setFlag('rental_junk', n);
          store.apply({ money: LANDLORD.junkPay });
          this.s.sfx('crunch');
          if (n >= JUNK_PIECES.length) {
            this.stage = 'cleared';
            store.toast(
              "That's the last of it. Lot 10's clear (Jerry's nephew even bush-hogged the weeds). Next: the sign on the lot. Call Big Dave about a trailer.",
              'good',
              ':shack:',
            );
          } else
            store.toast(
              `Jerry pays $${LANDLORD.junkPay} scrap. ${JUNK_PIECES.length - n} more to haul.`,
              'good',
              ':shack:',
            );
        }
      }
    }
    // Big Dave's crew shows up.
    if (this.stage === 'delivering' && store.now >= Number(store.flag('rental_arrive') ?? 0)) {
      this.stage = 'trailer';
      this.s.sfx('semiHorn');
      store.toast(
        "Big Dave's crew dropped a repo single-wide on Lot 10. It's... a project. Five things to fix.",
        'good',
        ':semi:',
      );
    }
    // Rent day: Rhonda pays Fridays at 5, same as everybody (and catches up if you slept through it).
    const paid = Number(store.flag('rental_week') ?? 0);
    if (this.stage === 'rented' && paid < store.week && store.now >= rentDue(paid + 1)) {
      store.setFlag('rental_week', paid + 1);
      store.apply({ money: LANDLORD.rent });
      store.incr('rentCollected', LANDLORD.rent);
      this.s.sfx('cash');
      store.toast(
        `Rhonda Mae paid her rent: +$${LANDLORD.rent}. All quarters. Some of them are arcade tokens.`,
        'good',
        ':trailer:',
      );
    }
    this.sync();
    this.s.world.lot10.update(dt, time);
  }

  objective(): { text: string; target: THREE.Vector3 | null } | null {
    const t = (v: { x: number; z: number }): THREE.Vector3 => this.tmp.set(v.x, 0, v.z);
    switch (this.stage) {
      case 'leased':
        return this.carrying
          ? {
              text: `:shack: Haul it to Junkyard Jerry's drop-off (${this.junk + 1}/${JUNK_PIECES.length})`,
              target: t(SPOTS.junkyardDrop),
            }
          : {
              text: `:shack: Haul the burnt junk off Lot 10 (${this.junk}/${JUNK_PIECES.length})`,
              target: t(LOT10.junk[this.junk] ?? LOT10.sign),
            };
      case 'cleared':
        return {
          text: ':telephone: Call Big Dave about a trailer (the sign on Lot 10)',
          target: t(LOT10.sign),
        };
      case 'trailer': {
        const next = RENTAL_FIXES.find((f) => !this.fixed.includes(f));
        return next
          ? {
              text: `:hammer: Fix up the Lot 10 trailer (${this.fixed.length}/${RENTAL_FIXES.length} done)`,
              target: t(LOT10.fixes[next]),
            }
          : null;
      }
      case 'ready':
        return {
          text: ':trailer: Lot 10 is ready: rent it out or sell it (the sign)',
          target: t(LOT10.sign),
        };
      default:
        return null;
    }
  }
}
